import React from 'react';
import { Paper, Box, Typography, Avatar, Chip } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MatchPlayer } from '../../models';
import PersonIcon from '@mui/icons-material/Person';
import { lightTheme } from '../../theme';

const LINEUP_STATUS_STYLE: Record<string, string> = {
  "": lightTheme.colors.palette.slate300,
  "PLAYING": lightTheme.colors.palette.green500,
  "NOT_PLAYING": lightTheme.colors.palette.red500,
  "UNAVAILABLE": lightTheme.colors.palette.red500,
  "LAST_PLAYED": lightTheme.colors.palette.blue500,
  "X_FACTOR_SUBSTITUTE": lightTheme.colors.palette.yellow500,
  "USED_X_FACTOR_SUBSTITUTE": lightTheme.colors.palette.yellow500,
};

interface SortablePlayerProps {
  matchPlayer: MatchPlayer;
  groupId: string;
  type: 'player';
}

const SortablePlayer: React.FC<SortablePlayerProps> = ({ matchPlayer, groupId, type }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: matchPlayer.id.toString(),
    data: {
      type,
      groupId,
    },
  });

  // console.log('SortablePlayer:', { 
  //   playerId: matchPlayer.id, 
  //   groupId, 
  //   type, 
  //   isDragging,
  //   transform
  // });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    touchAction: 'none',
  };

  const getTeamColor = (teamName: string) => {
    // Use a hash function to generate consistent colors for teams
    const hash = teamName.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 70%, 90%)`;
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        p: 1,
        mb: 1,
        cursor: 'grab',
        backgroundColor: getTeamColor(matchPlayer.team_name),
        '&:hover': {
          backgroundColor: getTeamColor(matchPlayer.team_name).replace('90%', '85%'),
        },
        '&:active': {
          cursor: 'grabbing',
        },
        userSelect: 'none',
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar sx={{ bgcolor: LINEUP_STATUS_STYLE[matchPlayer.lineup_status || ""] }} 
        src={matchPlayer.player.image_d ?? matchPlayer.player.image ?? undefined}>
          <PersonIcon />
        </Avatar>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle2">
              {matchPlayer.player.name}
            </Typography>
            <Chip
              label={matchPlayer.team_name}
              size="small"
              sx={{
                backgroundColor: getTeamColor(matchPlayer.team_name).replace('90%', '80%'),
                fontWeight: 'bold',
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">
              {matchPlayer.player.role}
            </Typography>
            {matchPlayer.prev_bat_pos && (
              <Typography variant="caption" color="text.secondary">
                BP: {matchPlayer.prev_bat_pos}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default SortablePlayer; 